import { useFilePanel } from '@/hooks/useFilePanel'
import {
  ChevronRight,
  Download,
  File,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Presentation,
} from 'lucide-react'
import { ReactNode } from 'react'
import { ContextMenuWrapper, type ContextAction } from './ContextMenuWrapper'
import { SpreadsheetPreview } from './SpreadsheetPreview'

interface FileData {
  name: string
  url?: string
  size?: number
  mimeType?: string
  downloadUrl?: string
  id?: string
}

interface Props {
  file: FileData
  showDownload?: boolean
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
  showPreview?: boolean
  parentBreadcrumbs?: Array<{ label: string; id: string; clickable: boolean }>
  customIcon?: ReactNode
  customLabel?: string
  customSubtitle?: string
  contextActions?: ContextAction[]
  onImageClick?: (url: string) => void
  className?: string
}

export function FileResult({
  file,
  showDownload = true,
  onOpenPanel,
  showPreview = false,
  parentBreadcrumbs = [],
  customIcon,
  customLabel,
  customSubtitle,
  contextActions = [],
  onImageClick,
  className = '',
}: Props) {
  // Set up file panel functionality
  const { openFileInPanel } = useFilePanel({
    onOpenPanel,
    parentBreadcrumbs,
  })

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
  }

  const getFileInfo = (mimeType?: string, url?: string) => {
    if (!mimeType && !url) return { type: 'file', icon: File, label: 'File', canPreview: false }

    if (
      mimeType?.startsWith('image/') ||
      url?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg)$/)
    ) {
      return { type: 'image', icon: FileImage, label: 'Image', canPreview: true }
    }
    if (
      mimeType?.startsWith('video/') ||
      url?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/)
    ) {
      return { type: 'video', icon: FileVideo, label: 'Video', canPreview: false }
    }
    if (mimeType?.startsWith('audio/') || url?.toLowerCase().match(/\.(mp3|wav|flac|aac|ogg)$/)) {
      return { type: 'audio', icon: FileAudio, label: 'Audio', canPreview: false }
    }
    if (mimeType?.includes('pdf') || url?.toLowerCase().includes('.pdf')) {
      return { type: 'pdf', icon: FileText, label: 'PDF Document', canPreview: true }
    }
    if (
      mimeType?.includes('document') ||
      mimeType?.includes('word') ||
      url?.toLowerCase().match(/\.(doc|docx)$/)
    ) {
      return { type: 'document', icon: FileText, label: 'Document', canPreview: true }
    }
    if (
      mimeType?.includes('spreadsheet') ||
      mimeType?.includes('excel') ||
      url?.toLowerCase().match(/\.(xls|xlsx|csv|tsv)$/)
    ) {
      return { type: 'spreadsheet', icon: FileSpreadsheet, label: 'Spreadsheet', canPreview: true }
    }
    if (
      mimeType?.includes('presentation') ||
      mimeType?.includes('powerpoint') ||
      url?.toLowerCase().match(/\.(ppt|pptx)$/)
    ) {
      return { type: 'presentation', icon: Presentation, label: 'Presentation', canPreview: true }
    }
    if (mimeType?.includes('text/plain') || url?.toLowerCase().match(/\.(txt|rtf)$/)) {
      return { type: 'text', icon: FileText, label: 'Text Document', canPreview: true }
    }

    return { type: 'file', icon: File, label: 'File', canPreview: false }
  }

  const handleHeaderClick = () => {
    if (onOpenPanel) {
      openFileInPanel(file)
    }
  }

  const fileInfo = getFileInfo(file.mimeType, file.url || file.downloadUrl)
  const IconComponent = fileInfo.icon
  const displayUrl = file.downloadUrl || file.url
  const displayName = customLabel || file.name
  const subtitle = customSubtitle || (file.size ? formatFileSize(file.size) : fileInfo.label)

  // Combine built-in download action with custom context actions
  const allContextActions: ContextAction[] = [...contextActions]
  if (showDownload && displayUrl) {
    allContextActions.unshift({
      id: 'download',
      label: 'Download',
      icon: <Download className="h-3 w-3" />,
      onClick: () => {
        const link = document.createElement('a')
        link.href = displayUrl
        link.download = file.name
        link.click()
      },
    })
  }

  return (
    <ContextMenuWrapper actions={allContextActions}>
      <div
        className={`overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm ${className}`}
      >
        {/* Header section */}
        <div className="group flex items-center gap-4 bg-white px-4 py-3">
          <div
            className={`flex min-w-0 flex-1 items-center gap-4 ${
              onOpenPanel ? 'cursor-pointer transition-colors hover:opacity-80' : ''
            }`}
            onClick={onOpenPanel ? handleHeaderClick : undefined}
          >
            <div className="flex-shrink-0">
              {customIcon || <IconComponent className="h-5 w-5 text-gray-600" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">{displayName}</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">{subtitle}</div>
            </div>
            {showDownload && displayUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  const link = document.createElement('a')
                  link.href = displayUrl
                  link.download = file.name
                  link.click()
                }}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {onOpenPanel && (
              <div className="relative">
                <ChevronRight className="h-4 w-4 text-gray-400 transition-all duration-200 group-hover:text-gray-600" />
                <ChevronRight className="absolute inset-0 h-4 w-4 -translate-x-1 text-gray-400 opacity-0 transition-all duration-200 group-hover:translate-x-1 group-hover:text-gray-600 group-hover:opacity-100" />
              </div>
            )}
          </div>
        </div>

        {/* Preview section - only show if showPreview is true and file can be previewed */}
        {showPreview && fileInfo.canPreview && displayUrl && (
          <div className="px-4 py-3">
            <div className="space-y-3">
              {fileInfo.type === 'image' ? (
                <div className="aspect-video overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={displayUrl}
                    alt={file.name}
                    className={`h-full w-full object-contain ${onImageClick ? 'cursor-pointer' : ''}`}
                    onClick={onImageClick ? () => onImageClick(displayUrl) : undefined}
                    onError={(e) => {
                      // If image fails to load, show a placeholder
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      const parent = target.parentElement
                      if (parent) {
                        parent.innerHTML =
                          '<div class="flex items-center justify-center h-full text-gray-500"><div class="text-center"><div class="h-12 w-12 mx-auto mb-2 bg-gray-300 rounded flex items-center justify-center">📷</div><p class="text-sm">Image preview unavailable</p></div></div>'
                      }
                    }}
                  />
                </div>
              ) : fileInfo.type === 'spreadsheet' ? (
                <SpreadsheetPreview url={displayUrl} maxRows={10} />
              ) : fileInfo.type === 'pdf' ? (
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
                  <iframe
                    src={displayUrl}
                    width="100%"
                    height="500px"
                    className="rounded-2xl"
                    title={file.name}
                  />
                </div>
              ) : fileInfo.type === 'document' ||
                fileInfo.type === 'presentation' ||
                fileInfo.type === 'text' ? (
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(displayUrl)}&embedded=true`}
                    width="100%"
                    height="500px"
                    className="rounded-2xl"
                    title={file.name}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </ContextMenuWrapper>
  )
}
